import {
  Box,
  Divider,
  Stack,
  Typography,
  CircularProgress,
  Fade,
} from "@mui/material";
import dayjs from "dayjs";
import { useEffect, useState, useMemo, useRef } from "react";
import { useForm } from "react-hook-form";
import { useLocation } from "react-router-dom";

import { GetAppointment, GetLocation, GetSchedule } from "../../services/fhir";
import { IFormInput } from "./types";

import { DisponibilityCard } from "./components/DisponibilityCard";

import { isMobile } from "react-device-detect";
import { LargeContentBox, NoDataWarning } from "../../components/common";
import { SearchFields } from "../../components/schedule/SearchFields";
import { currentFeatures, oids } from "../../configs/Settings";
import { useSessionContext } from "../../contexts/SessionContext";
import { useDatetime } from "../../utils/useDatetime";
import { useFhirData } from "../../utils/useFhirData";
import { Search } from "@mui/icons-material";
import { PageHeader } from "@/components/common/PageHeader";

export function SchedulingSearch() {
  const { state } = useLocation();
  const { user } = useSessionContext();
  const { formatDateToISO } = useDatetime;
  const [schedules, setSchedules] = useState<any>([]);
  const [location, setLocation] = useState<any>([]);
  const [loading, setLoading] = useState(false);
  const [initializing, setInitializing] = useState(false);
  const [availableSpecialties, setAvailableSpecialties] = useState<any[]>([]);
  const lastRequestIdRef = useRef(0);
  /**
   * Lista de organizações (references) derivadas do histórico do paciente.
   * Ex.: ["Organization/2.16.76.1.3.3-57877539000140", ...]
   *
   * Importante: mantemos em `ref` para não disparar buscas repetidas a cada submit.
   */
  const attendedOrgReferencesRef = useRef<string[] | null>(null);

  const defaultValues: IFormInput = {
    serviceModel: null,
    state: null,
    // city: null,
    date: dayjs(),
    healthcareService: state?.healthcareService || null,
    organization: state?.organization || null,
    practitioner: state?.practitioner || null,
  };

  const methods = useForm<IFormInput>({ defaultValues });
  const { handleSubmit, control, watch, getValues, resetField, setValue } =
    methods;

  const watchModel = watch("serviceModel");
  const watchState = watch("state");

  const organizationReferences = [
    "2.16.76.1.3.3-05490544000100",
    "2.16.76.1.3.3-1234567890123", // ID FLUXMED EM DEV
    "2.16.76.1.3.3-57877539000140", // ID FLUXMED EM PRODUÇÃO
  ];

  function isExtensionVirtualService(extension: fhir5.Extension) {
    return (
      extension.url ===
        "https://fluxmed.com.br/fhir/StructureDefinition/virtual-service" &&
      extension.valueBoolean === true
    );
  }

  function isVirtualService(location: fhir5.Location) {
    return !!location?.extension?.find(isExtensionVirtualService);
  }

  /** Retorna o valor da extension attendance-channel de uma Location, ou undefined. */
  function getAttendanceChannel(location: fhir5.Location): string | undefined {
    return (
      location?.extension?.find((ext) =>
        ext.url?.includes("attendance-channel"),
      ) as any
    )?.valueCode;
  }

  /**
   * Decide se uma Location deve aparecer para o modelo de atendimento selecionado:
   *  - virtualService  → virtual-service=true AND (canal=teleatendimento OR sem canal)
   *  - chatService     → virtual-service=true AND canal=chat
   *  - domicilioService→ canal=domicilio
   *  - physicalLocation→ sem virtual-service E sem canal domicílio/chat
   */
  function matchesModel(
    location: fhir5.Location,
    model: string | undefined,
  ): boolean {
    if (!model) return true;
    const channel = getAttendanceChannel(location);
    const virtual = isVirtualService(location);

    switch (model) {
      case "virtualService":
        return virtual && (channel === "teleatendimento" || !channel);
      case "chatService":
        return virtual && channel === "chat";
      case "domicilioService":
        return channel === "domicilio";
      case "physicalLocation":
        return !virtual && channel !== "domicilio" && channel !== "chat";
      default:
        return true;
    }
  }

  function buildPatientId(): string | undefined {
    // `useSchedulingSearch` habilita essa tela para pacientes, mas mantemos defensivo.
    const username = user?.username;
    if (!username) return undefined;

    // Mesmo padrão já usado em `ConfirmAppointmentDialog`:
    // - defaultApp => Patient/{oid.cpf}-{cpf}
    // - senão => Patient/{username}
    if (currentFeatures?.defaultApp) {
      const cpfDigits = String(username).replace(/\D/g, "");
      if (!cpfDigits) return undefined;
      return `${oids.cpf}-${cpfDigits}`;
    }

    return String(username);
  }

  function uniqueStrings(values: Array<string | undefined | null>): string[] {
    return Array.from(
      new Set(values.map((v) => (v ?? "").trim()).filter(Boolean)),
    );
  }

  function bundleEntriesOfType(
    bundle: fhir5.Bundle | undefined,
    resourceType: string,
  ): Array<fhir5.BundleEntry<fhir5.Resource>> {
    const entries = bundle?.entry ?? [];
    return entries.filter((e) => e?.resource?.resourceType === resourceType) as
      | Array<fhir5.BundleEntry<fhir5.Resource>>
      | [];
  }

  function extractLocationIdsFromAppointments(
    appointmentBundle: fhir5.Bundle | undefined,
  ): string[] {
    const appointmentEntries = bundleEntriesOfType(
      appointmentBundle,
      "Appointment",
    ) as Array<fhir5.BundleEntry<fhir5.Appointment>>;

    const refsFromParticipants = appointmentEntries
      .flatMap((entry) => entry.resource?.participant ?? [])
      .map((p) => p?.actor?.reference)
      .filter((ref): ref is string => Boolean(ref))
      .filter((ref) => ref.startsWith("Location/"))
      .map((ref) => ref.split("/")[1]);

    // Algumas implementações usam `Appointment.location` (nem sempre presente).
    const refsFromLocationField = appointmentEntries
      .map((entry: any) => entry?.resource?.location?.reference as string)
      .filter((ref): ref is string => Boolean(ref))
      .filter((ref) => ref.startsWith("Location/"))
      .map((ref) => ref.split("/")[1]);

    // Se o backend já incluiu Locations via `_include=Appointment:location`,
    // aproveitamos como fallback adicional (mais resiliente a variações de payload).
    const includedLocationIds = (
      bundleEntriesOfType(appointmentBundle, "Location") as Array<
        fhir5.BundleEntry<fhir5.Location>
      >
    )
      .map((e) => e.resource?.id)
      .filter((id): id is string => Boolean(id));

    return uniqueStrings([
      ...refsFromParticipants,
      ...refsFromLocationField,
      ...includedLocationIds,
    ]);
  }

  function extractManagingOrganizationReferencesFromLocations(
    locationBundle: fhir5.Bundle | undefined,
  ): string[] {
    const locationEntries = bundleEntriesOfType(
      locationBundle,
      "Location",
    ) as Array<fhir5.BundleEntry<fhir5.Location>>;

    const refs = locationEntries
      ?.map((e) => e.resource?.managingOrganization?.reference)
      ?.filter((ref): ref is string => Boolean(ref));

    return uniqueStrings(refs);
  }

  async function getAttendedOrganizationReferences(
    patientId: string | undefined,
  ): Promise<string[]> {
    if (!patientId) return [];

    try {
      // Fail-open + minimal: buscamos uma amostra grande o suficiente para cobrir histórico recente,
      // sem introduzir paginação complexa.
      const appointmentBundle = await GetAppointment({
        patient: patientId,
        includeLocation: true,
        count: 250,
        status: ["booked", "pending", "proposed", "arrived", "fulfilled"],
        // Não definimos janela de datas aqui para manter o comportamento simples e previsível.
        // Se virar problema de performance, a evolução deve introduzir start/endDate com cuidado.
      });

      const locationIds = extractLocationIdsFromAppointments(appointmentBundle);
      if (!locationIds.length) return [];

      const locationBundle = await GetLocation({
        ids: locationIds.join(","),
        includeOrganization: true,
      });

      return extractManagingOrganizationReferencesFromLocations(locationBundle);
    } catch (err) {
      console.warn(
        "[SchedulingSearch] Falha ao derivar organizações por histórico de atendimento do paciente. Mantendo busca sem restrição por organização.",
        err,
      );
      return [];
    }
  }

  function mergeBundles(
    bundles: Array<fhir5.Bundle | undefined>,
  ): fhir5.Bundle | undefined {
    const entries = bundles.flatMap((b) => b?.entry ?? []);
    if (!entries.length) return bundles.find((b) => b) ?? undefined;

    // Dedup por resourceType/id para evitar duplicar Locations/Schedules vindos de múltiplas orgs.
    const seen = new Set<string>();
    const deduped = entries.filter((e) => {
      const r: any = e?.resource;
      const key = r?.resourceType && r?.id ? `${r.resourceType}/${r.id}` : "";
      if (!key) return true;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });

    return {
      resourceType: "Bundle",
      type: "searchset",
      entry: deduped as any,
    } as fhir5.Bundle;
  }

  async function fetchResponse() {
    const currentModel = getValues("serviceModel");
    const currentState = getValues("state");

    // Se for presencial e não tiver estado selecionado, não busca
    if (currentModel?.value === "physicalLocation" && !currentState) {
      return;
    }

    const requestId = ++lastRequestIdRef.current;
    setLoading(true);
    // Mapeia os 4 modelos do frontend para os valores que a API conhece:
    //   chatService      → virtualService (são locations com virtual-service=true)
    //   domicilioService → sem filtro de modelo (a API não tem esse canal; filtramos no front)
    //   demais           → passam direto
    const rawModel = getValues("serviceModel.value");
    const apiServiceModel =
      rawModel === "chatService"
        ? "virtualService"
        : rawModel === "domicilioService"
          ? undefined
          : rawModel || undefined;

    const patientId = buildPatientId();
    const params: { [key: string]: string | undefined } = {
      serviceModel: apiServiceModel,
      state: getValues("state.value") || undefined,
      // city:
      //   getValues("city.label")?.split(" - ")?.[0] ||
      //   getValues("city.label") ||
      //   undefined,
      startDate: formatDateToISO(getValues("date") || dayjs()),
      specialty: getValues("healthcareService.label") || undefined,
      practitioner: getValues("practitioner.value") || undefined,
      organization: getValues("organization.value") || undefined,
    };

    Object.keys(params).forEach(
      (key) => params[key] === undefined && delete params[key],
    );

    // Regra nova: se conseguirmos derivar organizações atendidas, restringimos a busca a elas.
    // Fail-open: se não houver histórico/derivação falhar, mantemos comportamento padrão.
    const attendedOrgReferences =
      attendedOrgReferencesRef.current ??
      (await getAttendedOrganizationReferences(patientId));
    attendedOrgReferencesRef.current = attendedOrgReferences;

    const fetchSchedule =
      attendedOrgReferences.length > 0
        ? await GetSchedule({
            ...params,
            // `GetSchedule` aceita múltiplas organizações em CSV (OR).
            organization: attendedOrgReferences,
            includeLocation: true,
            active: true,
          })
        : await GetSchedule({
            ...params,
            includeLocation: true,
            active: true,
          });

    // Race guard: se uma busca mais nova começou, não sobrescrever estado com resposta antiga.
    if (lastRequestIdRef.current !== requestId) return;
    const filterSchedules = fetchSchedule?.entry?.filter(
      (el: fhir5.BundleEntry<fhir5.Resource>) =>
        el?.resource?.resourceType === "Schedule",
    );
    const filterLocations = fetchSchedule?.entry?.filter(
      (el: fhir5.BundleEntry<fhir5.Resource>) =>
        el?.resource?.resourceType === "Location",
    );

    // Extrai as especialidades únicas dos schedules retornados
    const specialtiesFromSchedules =
      filterSchedules
        ?.flatMap((schedule: any) => {
          // Encontra HealthcareService no array de actors
          return schedule?.resource?.actor?.filter((actor: any) =>
            actor?.reference?.startsWith("HealthcareService/"),
          );
        })
        ?.filter((actor: any) => actor) // Remove undefined/null
        ?.map((actor: any) => ({
          value: actor?.reference?.split("/")[1], // Extrai o ID do HealthcareService
          label: actor?.display, // Nome da especialidade
        }))
        ?.filter(
          (specialty: any, index: number, self: any[]) =>
            // Remove duplicatas baseado no value
            index === self.findIndex((s) => s.value === specialty.value),
        ) || [];

    setSchedules(filterSchedules);
    setLocation(filterLocations);
    setAvailableSpecialties(specialtiesFromSchedules);
    setLoading(false);
  }

  // Evita "loading travado" se um request for abandonado por race-guard.
  useEffect(() => {
    return () => {
      lastRequestIdRef.current += 1;
    };
  }, []);

  useEffect(() => {
    (async () => {
      // Pré-carrega a checagem na abertura da tela para não depender da primeira busca.
      const patientId = buildPatientId();
      setInitializing(true);
      attendedOrgReferencesRef.current =
        await getAttendedOrganizationReferences(patientId);
      setInitializing(false);

      if (state) fetchResponse();
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Limpa os resultados se o usuário limpar o filtro de modelo (voltar para o início)
  useEffect(() => {
    if (!watchModel) {
      setSchedules([]);
      setLocation([]);
    }
  }, [watchModel]);

  // Memoizar a lista de resultados para evitar re-cálculo e re-renderização durante loading
  const resultCards = useMemo(() => {
    const hasAttendedOrgRestriction =
      (attendedOrgReferencesRef.current?.length ?? 0) > 0;

    return location
      ?.filter((loc: any) => {
        // Se estamos restringindo por organizações derivadas do histórico do paciente,
        // não aplicamos o filtro legado por `organizationReferences` (evita conflito).
        const organizationFilter = hasAttendedOrgRestriction
          ? true
          : import.meta.env.VITE_ENV !== "production"
            ? loc
            : !organizationReferences.some((reference) =>
                loc?.resource?.managingOrganization?.reference?.includes(
                  reference,
                ),
              );

        // Usa a função unificada que cobre os 4 modelos de atendimento
        const modelFilter = matchesModel(loc?.resource, watchModel?.value);

        return organizationFilter && modelFilter;
      })
      ?.map((el: any) => {
        const auxSchedules = schedules?.filter((schedule: any) => {
          const scheduleLocation = useFhirData.findActor(
            schedule?.resource?.actor,
            "Location",
          );

          return scheduleLocation?.reference.includes(el?.resource?.id);
        });

        return (
          <DisponibilityCard
            // Mantemos a prop por compatibilidade; agora significa
            // "paciente tem histórico em alguma organização" (dinâmico).
            hasAttendedByTargetOrg={hasAttendedOrgRestriction}
            key={el?.resource?.id}
            location={el?.resource}
            schedules={auxSchedules}
            startDate={formatDateToISO(dayjs(getValues("date")) || dayjs())}
          />
        );
      });
  }, [location, schedules, watchModel]);

  return (
    <>
      <LargeContentBox>
        <Stack spacing={3} width="100%">
          <PageHeader
            title={isMobile ? "O que você precisa hoje?" : "Agendar consulta"}
            icon={Search}
            centered
          />

          <SearchFields
            resetField={resetField}
            onSubmit={fetchResponse}
            control={control}
            watch={watch}
            handleSubmit={handleSubmit}
            availableSpecialties={availableSpecialties}
            setValue={setValue}
            isSearching={loading || initializing}
          />

          {!isMobile && (
            <Divider sx={{ width: "100%", color: "neutral300.main", mt: 2 }} />
          )}

          {/* Lógica de Renderização: Loading vs Dados vs Vazio */}
          {(() => {
            const hasData = location?.length > 0;
            const showFullLoading = loading && !hasData;
            // Modelos que não precisam de estado: virtual, chat e domicílio
            const noStateRequired = [
              "virtualService",
              "chatService",
              "domicilioService",
            ];
            const canShowContent =
              watchModel &&
              (noStateRequired.includes(watchModel.value) || watchState);

            return (
              <>
                {/* Loading Full Screen (inicialização/primeira busca) */}
                {showFullLoading && canShowContent && (
                  <Fade in={true}>
                    <Stack
                      alignItems="center"
                      justifyContent="center"
                      sx={{
                        py: 8,
                        width: "100%",
                        bgcolor: "background.paper",
                        borderRadius: 2,
                        boxShadow: "0 4px 20px rgba(0,0,0,0.05)",
                      }}
                      spacing={3}
                    >
                      <Box position="relative" display="inline-flex">
                        <CircularProgress
                          size={60}
                          thickness={4}
                          sx={{ color: "primary.light" }}
                        />
                        <Box
                          sx={{
                            top: 0,
                            left: 0,
                            bottom: 0,
                            right: 0,
                            position: "absolute",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          <Search
                            sx={{
                              color: "primary.main",
                              fontSize: 24,
                              opacity: 0.7,
                            }}
                          />
                        </Box>
                      </Box>
                      <Typography
                        variant="body1"
                        color="text.secondary"
                        fontWeight="medium"
                        sx={{
                          animation: "pulse 1.5s infinite ease-in-out",
                          "@keyframes pulse": {
                            "0%": { opacity: 0.6 },
                            "50%": { opacity: 1 },
                            "100%": { opacity: 0.6 },
                          },
                        }}
                      >
                        {initializing
                          ? "Preparando sua busca..."
                          : "Encontrando os melhores horários para você..."}
                      </Typography>
                    </Stack>
                  </Fade>
                )}

                {/* Lista de Resultados (Mantida com opacidade durante loading subsequente) */}
                {hasData && canShowContent && (
                  <Box
                    sx={{
                      opacity: loading ? 0.6 : 1,
                      transition: "opacity 0.3s ease",
                      pointerEvents: loading ? "none" : "auto",
                    }}
                  >
                    {resultCards}
                  </Box>
                )}

                {/* No Data (Só mostra se terminou de carregar e não tem dados) */}
                {!loading && !hasData && canShowContent && (
                  <Stack>
                    <NoDataWarning />
                  </Stack>
                )}
              </>
            );
          })()}
        </Stack>
      </LargeContentBox>
    </>
  );
}
